import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { X, Plus, Trash2, UserCheck, Upload, FileText, ExternalLink, CheckCircle2, GripVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import InlineContactSearch from '../InlineContactSearch';
import TrimbleAddressAutocomplete from '../TrimbleAddressAutocomplete';
import { cn } from '@/lib/utils';
import { computeLoadGross } from '@/lib/invoiceHelpers';
import { calcRoute, bestStop } from '@/lib/pcmiler';
import { checkBookingEligibility } from '@/lib/driverEligibility';

const SectionHeader = ({ title }) => (
  <h4 className="text-xs font-semibold text-primary uppercase tracking-widest pt-2 pb-1 border-b border-border">{title}</h4>
);

export default function EditLoadModal({ load, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [stops, setStops] = useState([]);
  const [approvedDrivers, setApprovedDrivers] = useState([]);
  const [truckByEmail, setTruckByEmail] = useState({});
  const [trucks, setTrucks] = useState([]);
  const [assignedTruckId, setAssignedTruckId] = useState('');
  const [saving, setSaving] = useState(false);
  const [rateconFile, setRateconFile] = useState(null);
  const [milesVerified, setMilesVerified] = useState(false);
  const [showContactPicker, setShowContactPicker] = useState(null);
  const [pickupAddressError, setPickupAddressError] = useState('');
  const [dropoffAddressError, setDropoffAddressError] = useState('');
  const [extraFees, setExtraFees] = useState([]);

  useEffect(() => {
    client.entities.DriverProfile.filter({ is_active: true }, 'full_name', 100)
      .then(setApprovedDrivers)
      .catch(() => {});

    // Map each driver to the truck unit # they're currently assigned to
    client.entities.Asset.filter({ asset_type: 'Truck' }, '-created_date', 200)
      .then(list => {
        const active = list.filter(t => t.is_active !== false);
        const map = {};
        active.forEach(t => {
          if (t.assigned_driver_email) map[t.assigned_driver_email.toLowerCase()] = t.unit_number;
        });
        setTruckByEmail(map);
        setTrucks(active);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (load) {
      const parseAddress = (addr) => {
        const parts = (addr || '').split(',').map(s => s.trim());
        return { street: parts[0] || '', city: parts[1] || '', state: parts[2] || '', zip: parts[3] || '' };
      };
      const pickup = parseAddress(load.pickup_address);
      const dropoff = parseAddress(load.dropoff_address);
      setForm({
        trip_number: load.trip_number || '',
        assigned_driver_email: load.assigned_driver_email || '',
        assigned_driver_name: load.assigned_driver_name || '',
        status: load.status || 'Available',
        customer_name: load.customer_name || '',
        customer_email: load.customer_email || '',
        customer_contact_id: load.customer_contact_id || '',
        origin: load.origin || '',
        destination: load.destination || '',
        miles: load.miles ?? '',
        pickup_street: pickup.street,
        pickup_city: pickup.city,
        pickup_state: pickup.state,
        pickup_zip: pickup.zip,
        dropoff_street: dropoff.street,
        dropoff_city: dropoff.city,
        dropoff_state: dropoff.state,
        dropoff_zip: dropoff.zip,
        pickup_date: load.pickup_date || '',
        pickup_time_window: load.pickup_time_window || '',
        delivery_date: load.delivery_date || '',
        dropoff_time_window: load.dropoff_time_window || '',
        po_pickup_number: load.po_pickup_number || '',
        special_notes: load.special_notes || '',
        trailer_size: load.trailer_size || '',
        pallet_case_count: load.pallet_case_count || '',
        temperature: load.temperature || '',
        hazmat: load.hazmat || false,
        load_requirements: load.load_requirements || '',
        line_haul_rate: load.line_haul_rate ?? '',
        line_haul_rate_type: load.line_haul_rate_type || 'Flat Fee',
        accessorial_pay: load.accessorial_pay ?? 0,
        fuel_surcharge: load.fuel_surcharge ?? 0,
        fuel_surcharge_type: load.fuel_surcharge_type || 'Flat Fee',
        detention_fee: load.detention_fee ?? 0,
        lumper_fee: load.lumper_fee ?? 0,
        late_fee: load.late_fee ?? 0,
        stop_off_fee: load.stop_off_fee ?? 0,
        tarp_fee: load.tarp_fee ?? 0,
        layover_fee: load.layover_fee ?? 0,
        additional_fee: load.additional_fee ?? 0,
        additional_fee_label: load.additional_fee_label || '',
        extra_fees: load.extra_fees || '',
        invoice_advance: load.invoice_advance ?? 0,
        legal_disclaimer: load.legal_disclaimer || '',
        ratecon_url: load.ratecon_url || '',
        shipper_name: load.shipper_name || '',
        shipper_contact: load.shipper_contact || '',
        shipper_phone: load.shipper_phone || '',
        shipper_email: load.shipper_email || '',
        shipper_contact_id: load.shipper_contact_id || '',
        consignee_name: load.consignee_name || '',
        consignee_contact: load.consignee_contact || '',
        consignee_phone: load.consignee_phone || '',
        consignee_email: load.consignee_email || '',
        consignee_contact_id: load.consignee_contact_id || '',
      });
      try {
        const rawStops = JSON.parse(load.additional_stops || '[]');
        // Stops saved before the structured-fields change only have a
        // single free-text `address` string -- same backward-compat split
        // as pickup/dropoff addresses above, so editing an older load
        // doesn't show blank street/city/state/zip inputs.
        setStops(rawStops.map(s => {
          // _key is a purely client-side drag-and-drop identity (see
          // handleStopDragEnd below) -- never persisted, so a stop
          // loaded back from saved JSON never has one yet.
          if (s.street || s.city || s.state || s.zip) return { _key: crypto.randomUUID(), ...s };
          const parsed = parseAddress(s.address);
          return { _key: crypto.randomUUID(), company: s.company || '', po_number: s.po_number || '', ...parsed };
        }));
        setExtraFees(JSON.parse(load.extra_fees || '[]'));
      } catch {
        setStops([]);
        setExtraFees([]);
      }
    }
  }, [load]);

  const set = (key, value) => setForm(p => ({ ...p, [key]: value }));

  const buildAddressString = (street, city, state, zip) => {
    const parts = [street, city, state, zip].filter(Boolean);
    return parts.join(', ');
  };

  // Shared by both mileage-calc triggers below -- they only differ in
  // whether an unrecognized address should surface a visible warning
  // (validateAndCalcMiles, on manual entry) or fail silently
  // (triggerMileCalc, right after a confident autocomplete pick).
  //
  // `overrides` matters: every call site invokes this synchronously right
  // after one or more set(...) calls in the same handler. React batches
  // state updates, so `form` here is still the PRE-update snapshot --
  // reading e.g. form.dropoff_zip right after set('dropoff_zip', ...) in
  // the same tick sees the OLD (often empty) value, not the one just
  // picked. That silently failed the `!pickup_zip || !dropoff_zip` guard
  // below on whichever address was completed last, so the mileage calc
  // never actually ran (confirmed: zero real invocations reached the
  // network despite users completing both addresses). Callers pass the
  // fields they just set as `overrides` so this always sees current data.
  const calcMiles = async ({ showErrors, overrides = {} } = {}) => {
    const f = { ...form, ...overrides };
    if (!f.pickup_zip || !f.dropoff_zip) return;
    // Additional Stops (drag-reorderable, below) ride between the
    // primary pickup and dropoff in visit order -- previously this
    // only ever routed a straight pickup->dropoff line, so Miles
    // silently ignored every additional stop's real detour. A stop
    // with no city/state yet is skipped rather than breaking the route.
    const stopPoints = stops
      .filter(s => s.city && s.state)
      .map(s => ({ street: s.street, city: s.city, state: s.state, zip: s.zip }));
    const result = await calcRoute(
      [
        { street: f.pickup_street, city: f.pickup_city, state: f.pickup_state, zip: f.pickup_zip },
        ...stopPoints,
        { street: f.dropoff_street, city: f.dropoff_city, state: f.dropoff_state, zip: f.dropoff_zip },
      ],
      { hazmat: f.hazmat }
    );
    if (result.error || !(result.miles > 0)) {
      const msg = result.error || 'Address not recognized by PCMiler — please verify.';
      if (showErrors) {
        setPickupAddressError(`⚠ ${msg}`);
        setDropoffAddressError(`⚠ ${msg}`);
      } else {
        console.error('Miles calc failed:', msg);
        toast.error(msg, { duration: 10000 });
      }
      return;
    }
    set('miles', String(Math.round(result.miles)));
    setMilesVerified(true);
    if (showErrors) {
      setPickupAddressError('');
      setDropoffAddressError('');
    }
  };

  const triggerMileCalc = (overrides) => calcMiles({ showErrors: false, overrides });
  const validateAndCalcMiles = (overrides) => calcMiles({ showErrors: true, overrides });

  // Recalculates Miles whenever a stop's address changes OR the stops
  // are reordered (drag-and-drop, below) -- keyed off just the
  // address-relevant fields in array order, so editing a stop's
  // Company Name or PO # (unrelated to the route) doesn't refire this
  // on every keystroke. Runs after the stops state has actually
  // committed, so it always sees the true final array.
  const stopsRouteSignature = JSON.stringify(stops.map(s => [s.city, s.state, s.zip]));
  useEffect(() => {
    if (form.pickup_zip && form.dropoff_zip) triggerMileCalc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopsRouteSignature]);

  // For an already-assigned load being edited, default the truck picker to the
  // truck this driver is currently on. Must stay above the `if (!load)`
  // early return below -- React requires every hook to run in the same
  // order on every render, and this one was previously called
  // conditionally (skipped whenever `load` was falsy), a real
  // react-hooks/rules-of-hooks violation even though the current single
  // caller (AdminPanel's `{editLoad && <EditLoadModal load={editLoad} .../>}`)
  // happens to never mount this component with a falsy `load`.
  useEffect(() => {
    if (!load) return;
    if (form.assigned_driver_email && trucks.length && !assignedTruckId) {
      const t = trucks.find(x => (x.assigned_driver_email || '').toLowerCase() === form.assigned_driver_email.toLowerCase());
      if (t) setAssignedTruckId(t.id);
    }
  }, [load, trucks, form.assigned_driver_email, assignedTruckId]);

  if (!load) return null;

  const handleDriverSelect = (email) => {
    if (!email) {
      set('assigned_driver_email', '');
      set('assigned_driver_name', '');
      set('status', 'Available');
      set('assignment_status', null);
      setAssignedTruckId('');
      return;
    }
    const driver = approvedDrivers.find(d => d.user_email === email);
    const { eligible, reasons } = checkBookingEligibility(driver, email, trucks);
    if (!eligible) {
      toast.error(`Can't book ${driver?.full_name || email} — ${reasons.join(' ')}`, { duration: 6000 });
      return;
    }
    set('assigned_driver_email', email);
    set('assigned_driver_name', driver?.full_name || '');
    set('status', 'Booked');
    set('assignment_status', null);
    const curTruck = trucks.find(t => (t.assigned_driver_email || '').toLowerCase() === email.toLowerCase());
    setAssignedTruckId(curTruck?.id || '');
  };

  const addStop = () => setStops(s => [...s, { _key: crypto.randomUUID(), company: '', street: '', city: '', state: '', zip: '', po_number: '' }]);
  const removeStop = (i) => setStops(s => s.filter((_, idx) => idx !== i));
  const updateStop = (i, key, value) => setStops(s => s.map((stop, idx) => idx === i ? { ...stop, [key]: value } : stop));
  const handleStopDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    setStops(s => {
      const next = Array.from(s);
      const [moved] = next.splice(result.source.index, 1);
      next.splice(result.destination.index, 0, moved);
      return next;
    });
  };

  const grossRate = computeLoadGross(form);

  const handleSave = async () => {
    setSaving(true);
    // Previously had no try/catch -- a failure anywhere below (upload,
    // save, geocoding) threw and left setSaving(true) stuck forever
    // with no error shown and no way to retry short of closing the
    // modal. See also the pickup_date/delivery_date sanitization below:
    // those are real `date`-typed columns, and Postgres rejects an
    // empty string with a 400 -- exactly what silently killed saves
    // with a blank date field before this try/catch existed to even
    // surface it.
    try {
    const lineHaul = parseFloat(form.line_haul_rate) || 0;
    const accessorial = parseFloat(form.accessorial_pay) || 0;
    const pickup_address = buildAddressString(form.pickup_street, form.pickup_city, form.pickup_state, form.pickup_zip);
    const dropoff_address = buildAddressString(form.dropoff_street, form.dropoff_city, form.dropoff_state, form.dropoff_zip);
    // Only rebuild from city/state when both are actually present --
    // otherwise fall back to whatever origin/destination the form
    // already has instead of clobbering it with "undefined, undefined".
    const origin = (form.pickup_city && form.pickup_state) ? `${form.pickup_city}, ${form.pickup_state}` : (form.origin || '');
    const destination = (form.dropoff_city && form.dropoff_state) ? `${form.dropoff_city}, ${form.dropoff_state}` : (form.destination || '');
    let ratecon_url = form.ratecon_url;
    if (rateconFile) {
      const uploaded = await client.integrations.Core.UploadFile({ file: rateconFile });
      ratecon_url = uploaded.file_url;
    }
    // Snapshot which unit is actually hauling this load right now -- see
    // NewLoadModal.jsx's handleCreate for why (unit-level reports used to
    // infer this from the driver's CURRENT truck assignment instead).
    const assignedTruckUnit = assignedTruckId ? trucks.find(t => t.id === assignedTruckId)?.unit_number : undefined;
    await client.entities.Load.update(load.id, {
      ...form,
      pickup_address,
      dropoff_address,
      origin,
      destination,
      truck_unit: assignedTruckUnit || form.truck_unit || undefined,
      pickup_date: form.pickup_date || null,
      delivery_date: form.delivery_date || null,
      // Same category of bug as pickup_date/delivery_date above, but for
      // uuid-typed columns: these reset to '' whenever the customer/
      // shipper/consignee name field is typed without picking a match
      // from the InlineContactSearch autocomplete, and Postgres rejects
      // an empty string for a uuid column just as it does for a date.
      customer_contact_id: form.customer_contact_id || null,
      shipper_contact_id: form.shipper_contact_id || null,
      consignee_contact_id: form.consignee_contact_id || null,
      line_haul_rate: lineHaul,
      accessorial_pay: accessorial,
      rate: grossRate,
      // See NewLoadModal.jsx's handleCreate for why `address` is computed
      // here alongside the structured fields (ActiveLoad.jsx's driver
      // view reads stop.address directly).
      additional_stops: stops.length > 0
        ? JSON.stringify(stops.map(({ _key, ...s }) => ({ ...s, address: buildAddressString(s.street, s.city, s.state, s.zip) })))
        : '',
      extra_fees: extraFees.length > 0 ? JSON.stringify(extraFees) : '',
      ratecon_url,
    });

    // If an Invoice was already auto-created for this load (on delivery --
    // see ActiveLoad.jsx), its total_amount was computed at that moment and
    // would go stale if a dispatcher edits fees afterward while reviewing
    // before flipping status to 'Invoiced'. Re-sync it here, but only while
    // it's still Unpaid -- never touch an already-finalized (Paid/Void)
    // invoice's amount.
    try {
      const linkedInvoices = await client.entities.Invoice.filter({ load_id: load.id });
      const openInvoice = linkedInvoices.find(inv => inv.status === 'Unpaid');
      if (openInvoice) {
        await client.entities.Invoice.update(openInvoice.id, {
          total_amount: grossRate,
          balance_due: grossRate,
        });
      }
    } catch (e) {
      console.error('Failed to re-sync linked invoice amount:', e);
    }

    // IFTA mileage log — ActiveLoad.jsx's own "Mark as Delivered" already
    // creates one, but a dispatcher can also flip a load straight to
    // Delivered from here (the Status Override above), which used to skip
    // mileage tracking entirely -- silently missing loads from IFTA
    // reports with no way to backfill except the driver never having used
    // this path at all. Guarded on an existing log for this load so
    // re-saving an already-Delivered load, or one the driver already
    // logged, never double-creates one. Never blocks the save if it fails.
    if (form.status === 'Delivered' && !['Delivered', 'Invoiced', 'Claim'].includes(load.status)) {
      try {
        const existingLogs = await client.entities.IFTAMileageLog.filter({ load_id: load.id });
        if (existingLogs.length === 0) {
          const today = new Date();
          const quarter = `Q${Math.ceil((today.getMonth() + 1) / 3)}-${today.getFullYear()}`;
          let stateMileage = [];
          let iftaSource = 'Manual';
          if (origin && destination) {
            const result = await calcRoute(
              [bestStop(pickup_address, origin), bestStop(dropoff_address, destination)],
              { includeStates: true }
            );
            if (!result.error && result.stateMileage?.length > 0) {
              stateMileage = result.stateMileage;
              iftaSource = 'PCMiler';
            }
          }
          const totalIftaMiles = stateMileage.reduce((sum, s) => sum + s.miles, 0);
          await client.entities.IFTAMileageLog.create({
            load_id: load.id,
            load_number: load.load_number,
            driver_email: form.assigned_driver_email || load.assigned_driver_email || '',
            origin,
            destination,
            trip_date: today.toISOString().split('T')[0],
            quarter,
            year: today.getFullYear(),
            state_mileage: JSON.stringify(stateMileage),
            total_miles: totalIftaMiles,
            source: iftaSource,
          });
        }
      } catch (e) {
        console.error('Failed to create IFTA mileage log on delivery:', e);
      }
    }

    // Same trigger as AddInvoiceModal's: once a load reaches Invoiced,
    // its settlement should be ready for payroll instead of sitting
    // with no visible status/action in the Driver Settlement tab.
    if (form.status === 'Invoiced' && load.status !== 'Invoiced') {
      try {
        const linkedSettlements = await client.entities.Settlement.filter({ load_id: load.id });
        await Promise.all(linkedSettlements
          .filter(s => s.status !== 'Paid' && s.status !== 'In Batch')
          .map(s => client.entities.Settlement.update(s.id, { status: 'In Batch' })));
      } catch (e) {
        console.error('Failed to move settlement into payroll batch:', e);
      }
    }

    // Keep the truck Asset's assigned driver in sync (source of truth used by
    // the owner portal & dispatch).
    if (assignedTruckId && form.assigned_driver_email) {
      const t = trucks.find(x => x.id === assignedTruckId);
      if (t && (t.assigned_driver_email || '').toLowerCase() !== form.assigned_driver_email.toLowerCase()) {
        try {
          await client.entities.Asset.update(assignedTruckId, { assigned_driver_email: form.assigned_driver_email });
        } catch {
          toast.error("Load updated, but syncing the truck's assigned driver failed — set it in the Assets tab.");
        }
      }
    }
    toast.success('Load details updated.');
    onSaved();
    onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to save load. Please try again.', { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const field = (label, key, type = 'text') => (
    <div>
      <label className="text-xs text-muted-foreground block mb-1">{label}</label>
      <Input
        type={type}
        value={form[key] ?? ''}
        onChange={e => set(key, e.target.value)}
        className="text-foreground"
      />
    </div>
  );

  const textarea = (label, key, rows = 2) => (
    <div>
      <label className="text-xs text-muted-foreground block mb-1">{label}</label>
      <textarea
        value={form[key] ?? ''}
        onChange={e => set(key, e.target.value)}
        rows={rows}
        className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary/50"
      />
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-start justify-center p-4">
      <div className="relative w-full max-w-3xl my-8 rounded-2xl border border-border p-6 space-y-5 bg-background">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-space font-semibold text-foreground">Edit Load Details</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Load #{load.load_number} — {load.origin} → {load.destination}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Status Override */}
        <SectionHeader title="Load Status" />
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Status</label>
          <select
            value={form.status ?? load.status ?? 'Available'}
            onChange={e => set('status', e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm"
          >
            {['Available', 'Booked', 'In Transit', 'Delivered', 'Invoiced', 'Claim'].map(s => (
              <option key={s} value={s} className="bg-background">{s}</option>
            ))}
          </select>
        </div>

        {/* Customer */}
        <div>
          <SectionHeader title="Customer / Broker" />
          <div className="mt-3 space-y-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">Customer / Broker Name</label>
              <InlineContactSearch
                value={form.customer_name ?? ''}
                onChange={e => { set('customer_name', e); set('customer_contact_id', ''); }}
                contactType="Customer"
                onSelect={data => {
                  set('customer_name', data.name);
                  set('customer_email', data.email);
                  set('customer_contact_id', data.id || '');
                }}
                placeholder="Search or add customer"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">Email</label>
              <Input
                type="email"
                value={form.customer_email ?? ''}
                onChange={e => set('customer_email', e.target.value)}
                className="text-foreground"
              />
            </div>
          </div>
        </div>

        {/* Driver Assignment */}
        <SectionHeader title="Driver Assignment" />
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Assign to Driver</label>
          <div className="relative">
            <UserCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <select
              value={form.assigned_driver_email}
              onChange={e => handleDriverSelect(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm"
            >
              <option value="" className="bg-background text-muted-foreground">Unassigned (Post to Load Board)</option>
              {approvedDrivers.map(d => (
                <option key={d.id} value={d.user_email} className="bg-background">
                  {d.full_name} — {truckByEmail[d.user_email?.toLowerCase()] ? `Unit ${truckByEmail[d.user_email?.toLowerCase()]}` : 'No truck assigned'}
                </option>
              ))}
            </select>
          </div>
          {form.assigned_driver_email && (
            <>
              <div className="mt-3">
                <label className="text-xs text-muted-foreground block mb-1">Truck / Unit (syncs this driver onto the truck)</label>
                <select
                  value={assignedTruckId}
                  onChange={e => setAssignedTruckId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm"
                >
                  <option value="" className="bg-background text-muted-foreground">— No truck / leave as is —</option>
                  {trucks.map(t => (
                    <option key={t.id} value={t.id} className="bg-background">
                      Unit {t.unit_number} — {[t.year, t.make, t.model].filter(Boolean).join(' ') || 'Truck'}{t.assigned_driver_email && t.assigned_driver_email.toLowerCase() !== form.assigned_driver_email.toLowerCase() ? ` · now: ${t.assigned_driver_email}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-amber-400 mt-1.5">Status will be set to <strong>Booked</strong> automatically.{assignedTruckId ? ' This driver will be set as the truck’s assigned driver.' : ''}</p>
            </>
          )}
        </div>

        {/* Routing & Schedule */}
        <div className="space-y-4">
          {/* Row 1: Load Number / Trip Number */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Load #</label>
              <Input
                value={form.load_number ?? load.load_number ?? ''}
                onChange={e => set('load_number', e.target.value)}
                className="text-foreground"
                readOnly
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Trip #</label>
              <Input
                value={form.trip_number ?? ''}
                onChange={e => set('trip_number', e.target.value)}
                className="text-foreground"
              />
            </div>
          </div>

          {/* Shipper Section */}
          <div className="mb-4 p-4 rounded-xl bg-muted/40 border border-border space-y-3">
            <SectionHeader title="Shipper" />
            <div className="mb-3 mt-3">
              <InlineContactSearch
                value={form.shipper_name ?? ''}
                onChange={e => { set('shipper_name', e); set('shipper_contact_id', ''); }}
                contactType="Shipper"
                onSelect={data => {
                  set('shipper_name', data.name);
                  set('shipper_contact', data.contact);
                  set('shipper_phone', data.phone);
                  set('shipper_email', data.email);
                  set('shipper_contact_id', data.id || '');
                  set('pickup_street', data.street);
                  set('pickup_city', data.city);
                  set('pickup_state', data.state);
                  set('pickup_zip', data.zip);
                  validateAndCalcMiles({ pickup_street: data.street, pickup_city: data.city, pickup_state: data.state, pickup_zip: data.zip });
                }}
                placeholder="Search or add shipper"
              />
            </div>
            <div className="grid grid-cols-[1fr_1fr_120px] gap-2">
              <Input
                value={form.shipper_contact ?? ''}
                onChange={e => set('shipper_contact', e.target.value)}
                placeholder="Contact Person"
                className="text-foreground"
              />
              <Input
                value={form.shipper_phone ?? ''}
                onChange={e => set('shipper_phone', e.target.value)}
                placeholder="Phone"
                className="text-foreground"
              />
              <Input
                value={form.shipper_email ?? ''}
                onChange={e => set('shipper_email', e.target.value)}
                placeholder="Email"
                type="email"
                className="text-foreground"
              />
            </div>
          </div>

          {/* Row 2: Pickup Address */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="text-xs font-semibold text-foreground">Pickup Address</label>
            </div>
            <TrimbleAddressAutocomplete
              value={form.pickup_street ?? ''}
              onChange={e => { set('pickup_street', e.target.value); setPickupAddressError(''); }}
              onSelect={data => {
                set('pickup_city', data.city || '');
                set('pickup_state', data.state || '');
                set('pickup_zip', data.zip || '');
                triggerMileCalc({ pickup_city: data.city || '', pickup_state: data.state || '', pickup_zip: data.zip || '' });
              }}
              label=""
              placeholder="Street address"
            />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="sm:col-span-2">
                <Input
                  value={form.pickup_city ?? ''}
                  onChange={e => { set('pickup_city', e.target.value); setPickupAddressError(''); }}
                  placeholder="City"
                  className="text-foreground"
                />
              </div>
              <Input
                value={form.pickup_state ?? ''}
                onChange={e => { set('pickup_state', e.target.value); setPickupAddressError(''); }}
                placeholder="State"
                className="text-foreground"
                maxLength="2"
              />
              <Input
                value={form.pickup_zip ?? ''}
                onChange={e => { set('pickup_zip', e.target.value); setPickupAddressError(''); }}
                placeholder="ZIP"
                className="text-foreground"
                onBlur={() => validateAndCalcMiles()}
              />
            </div>
            {pickupAddressError && <p className="text-xs text-red-400 mt-1">{pickupAddressError}</p>}
          </div>

          {/* Consignee Section */}
          <div className="mb-4 p-4 rounded-xl bg-muted/40 border border-border space-y-3">
            <SectionHeader title="Consignee" />
            <div className="mb-3 mt-3">
              <InlineContactSearch
                value={form.consignee_name ?? ''}
                onChange={e => { set('consignee_name', e); set('consignee_contact_id', ''); }}
                contactType="Consignee"
                onSelect={data => {
                  set('consignee_name', data.name);
                  set('consignee_contact', data.contact);
                  set('consignee_phone', data.phone);
                  set('consignee_email', data.email);
                  set('consignee_contact_id', data.id || '');
                  set('dropoff_street', data.street);
                  set('dropoff_city', data.city);
                  set('dropoff_state', data.state);
                  set('dropoff_zip', data.zip);
                  validateAndCalcMiles({ dropoff_street: data.street, dropoff_city: data.city, dropoff_state: data.state, dropoff_zip: data.zip });
                }}
                placeholder="Search or add consignee"
              />
            </div>
            <div className="grid grid-cols-[1fr_1fr_120px] gap-2">
              <Input
                value={form.consignee_contact ?? ''}
                onChange={e => set('consignee_contact', e.target.value)}
                placeholder="Contact Person"
                className="text-foreground"
              />
              <Input
                value={form.consignee_phone ?? ''}
                onChange={e => set('consignee_phone', e.target.value)}
                placeholder="Phone"
                className="text-foreground"
              />
              <Input
                value={form.consignee_email ?? ''}
                onChange={e => set('consignee_email', e.target.value)}
                placeholder="Email"
                type="email"
                className="text-foreground"
              />
            </div>
          </div>

          {/* Row 3: Drop-off Address */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="text-xs font-semibold text-foreground">Drop-off Address</label>
            </div>
            <TrimbleAddressAutocomplete
              value={form.dropoff_street ?? ''}
              onChange={e => { set('dropoff_street', e.target.value); setDropoffAddressError(''); }}
              onSelect={data => {
                set('dropoff_city', data.city || '');
                set('dropoff_state', data.state || '');
                set('dropoff_zip', data.zip || '');
                triggerMileCalc({ dropoff_city: data.city || '', dropoff_state: data.state || '', dropoff_zip: data.zip || '' });
              }}
              label=""
              placeholder="Street address"
            />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="sm:col-span-2">
                <Input
                  value={form.dropoff_city ?? ''}
                  onChange={e => { set('dropoff_city', e.target.value); setDropoffAddressError(''); }}
                  placeholder="City"
                  className="text-foreground"
                />
              </div>
              <Input
                value={form.dropoff_state ?? ''}
                onChange={e => { set('dropoff_state', e.target.value); setDropoffAddressError(''); }}
                placeholder="State"
                className="text-foreground"
                maxLength="2"
              />
              <Input
                value={form.dropoff_zip ?? ''}
                onChange={e => { set('dropoff_zip', e.target.value); setDropoffAddressError(''); }}
                placeholder="ZIP"
                className="text-foreground"
                onBlur={() => validateAndCalcMiles()}
              />
            </div>
            {dropoffAddressError && <p className="text-xs text-red-400 mt-1">{dropoffAddressError}</p>}
          </div>

          {/* Row 4: Pickup Date | Pickup Time Window | Delivery Date */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {field('Pickup Date', 'pickup_date', 'date')}
            {field('Pickup Time Window', 'pickup_time_window')}
            {field('Delivery Date', 'delivery_date', 'date')}
          </div>

          {/* Row 5: Drop-off Time Window (1/3) | Miles (2/3) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {field('Drop-off Time Window', 'dropoff_time_window')}
            <div className="sm:col-span-2">
              <label className="text-xs text-muted-foreground block mb-1">Miles</label>
              <div className="relative">
                <Input
                  type="number"
                  value={form.miles ?? load.miles ?? ''}
                  onChange={e => { set('miles', e.target.value); setMilesVerified(false); }}
                  readOnly
                  className="text-foreground cursor-default"
                  placeholder="Auto-filled when both cities selected"
                />
                {milesVerified && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-green-400 text-xs font-medium pointer-events-none">
                    <CheckCircle2 className="w-3.5 h-3.5" /> PC*Miler ✓
                  </span>
                )}
              </div>
            </div>
          </div>

          {field('PO / Pickup Number', 'po_pickup_number')}
        </div>

        {/* Additional Stops */}
        <SectionHeader title="Additional Stops" />
        {stops.length > 1 && (
          <p className="text-xs text-muted-foreground -mt-1">Drag stops into the order they're actually visited — Miles recalculates to match.</p>
        )}
        <DragDropContext onDragEnd={handleStopDragEnd}>
          <Droppable droppableId="additional-stops">
            {(droppableProvided) => (
              <div className="space-y-3" ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                {stops.map((stop, i) => (
                  <Draggable key={stop._key} draggableId={stop._key} index={i}>
                    {(draggableProvided, draggableSnapshot) => (
                      <div
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        className={cn(
                          "relative p-4 rounded-xl bg-muted/40 border border-border space-y-3",
                          draggableSnapshot.isDragging && "ring-2 ring-primary/50 shadow-xl"
                        )}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5">
                            <span
                              {...draggableProvided.dragHandleProps}
                              className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground -ml-1 p-0.5 touch-none"
                              title="Drag to reorder"
                            >
                              <GripVertical className="w-4 h-4" />
                            </span>
                            <span className="text-xs font-semibold text-primary">Stop {i + 1}</span>
                          </div>
                          <button type="button" onClick={() => removeStop(i)} className="text-red-400 hover:text-red-300 transition-colors p-1 rounded hover:bg-red-500/10">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {/* Mirrors the Pickup/Drop-off Address blocks above (street
                            autocomplete + city/state/zip grid) instead of a single
                            free-text address field, so an additional stop looks and
                            behaves the same as the primary pickup/drop-off. */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-muted-foreground block mb-1">Company Name</label>
                            <Input value={stop.company} onChange={e => updateStop(i, 'company', e.target.value)} className="text-foreground" placeholder="e.g. Walmart DC" />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground block mb-1">PO #</label>
                            <Input value={stop.po_number} onChange={e => updateStop(i, 'po_number', e.target.value)} className="text-foreground" placeholder="PO-XXXXXX" />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground block mb-1">Street Address</label>
                          <TrimbleAddressAutocomplete
                            value={stop.street}
                            onChange={e => updateStop(i, 'street', e.target.value)}
                            onSelect={data => {
                              updateStop(i, 'city', data.city || '');
                              updateStop(i, 'state', data.state || '');
                              updateStop(i, 'zip', data.zip || '');
                            }}
                            label=""
                            placeholder="Street address"
                          />
                        </div>
                        <div className="grid grid-cols-[1fr_120px_100px] gap-2">
                          <Input value={stop.city} onChange={e => updateStop(i, 'city', e.target.value)} placeholder="City" className="text-foreground" />
                          <Input value={stop.state} onChange={e => updateStop(i, 'state', e.target.value)} placeholder="State" className="text-foreground" maxLength="2" />
                          <Input value={stop.zip} onChange={e => updateStop(i, 'zip', e.target.value)} placeholder="ZIP" className="text-foreground" />
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <button
          type="button"
          onClick={addStop}
          className="w-full py-3 mt-3 rounded-xl border-2 border-dashed border-primary/50 text-primary text-sm font-semibold hover:bg-primary/5 transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add Stop
        </button>

        {textarea('Special Notes', 'special_notes')}

        {/* Freight Specifications */}
        <SectionHeader title="Freight Specifications" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {field('Trailer Size (e.g. 53 ft)', 'trailer_size')}
          {field('Pallet / Case Count', 'pallet_case_count')}
          {field('Temperature', 'temperature')}
        </div>
        {textarea('Load Requirements (e.g. Drop Trailer, Power Only)', 'load_requirements')}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => set('hazmat', !form.hazmat)}
            className={`relative w-10 h-5 rounded-full transition-colors ${form.hazmat ? 'bg-red-500' : 'bg-muted-foreground/30'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.hazmat ? 'translate-x-5' : 'translate-x-0'}`} />
          </button>
          <label className="text-sm text-foreground">Hazmat Load</label>
        </div>

        {/* Fees & Charges */}
        <SectionHeader title="Fees & Charges" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Primary Fee */}
          <div className="space-y-4">
            <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide">Primary Fee</h4>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Line Haul Rate Type</label>
              <select
                value={form.line_haul_rate_type || 'Flat Fee'}
                onChange={e => set('line_haul_rate_type', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm"
              >
                <option value="Flat Fee" className="bg-background">Flat Fee</option>
                <option value="Per Mile" className="bg-background">Per Mile</option>
                <option value="Percent" className="bg-background">Percent</option>
              </select>
            </div>
            {field('Line Haul Rate ($)', 'line_haul_rate', 'number')}
            <div>
              <label className="text-xs text-muted-foreground block mb-1">FSC Amount Type</label>
              <select
                value={form.fuel_surcharge_type || 'Flat Fee'}
                onChange={e => set('fuel_surcharge_type', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm"
              >
                <option value="Flat Fee" className="bg-background">Flat Fee</option>
                <option value="Per Mile" className="bg-background">Per Mile</option>
                <option value="Percent" className="bg-background">Percent</option>
              </select>
            </div>
            {field('Fuel Surcharge ($)', 'fuel_surcharge', 'number')}
          </div>

          {/* Accessory Fees */}
          <div className="space-y-4">
            <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide">Accessory Fees</h4>
            {field('Detention ($)', 'detention_fee', 'number')}
            {field('Layover ($)', 'layover_fee', 'number')}
            {field('Stop Off ($)', 'stop_off_fee', 'number')}
            {field('Tarp Fee ($)', 'tarp_fee', 'number')}
            {field('Lumper ($)', 'lumper_fee', 'number')}
            {field('Late Fee ($)', 'late_fee', 'number')}
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Additional Fee Label</label>
              <Input
                value={form.additional_fee_label || ''}
                onChange={e => set('additional_fee_label', e.target.value)}
                placeholder="e.g. Dock Fee"
                className="text-foreground"
              />
            </div>
            {field('Additional Fee ($)', 'additional_fee', 'number')}
          </div>
        </div>

        {/* Extra Fees */}
        {extraFees.length > 0 && (
          <div className="mt-4 p-4 rounded-xl bg-muted/40 border border-border space-y-3">
            {extraFees.map((fee, i) => (
              <div key={i} className="flex gap-3 items-end">
                <Input
                  value={fee.label}
                  onChange={e => {
                    const updated = [...extraFees];
                    updated[i].label = e.target.value;
                    setExtraFees(updated);
                    set('extra_fees', JSON.stringify(updated));
                  }}
                  placeholder="Fee label"
                  className="flex-1 text-foreground"
                />
                <Input
                  type="number"
                  value={fee.amount}
                  onChange={e => {
                    const updated = [...extraFees];
                    updated[i].amount = parseFloat(e.target.value) || 0;
                    setExtraFees(updated);
                    set('extra_fees', JSON.stringify(updated));
                  }}
                  placeholder="0.00"
                  className="w-24 text-foreground"
                />
                <button
                  type="button"
                  onClick={() => {
                    const updated = extraFees.filter((_, idx) => idx !== i);
                    setExtraFees(updated);
                    set('extra_fees', JSON.stringify(updated));
                  }}
                  className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 text-xs font-medium"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={() => {
            const updated = [...extraFees, { label: '', amount: 0 }];
            setExtraFees(updated);
            set('extra_fees', JSON.stringify(updated));
          }}
          className="mt-2 px-3 py-2 rounded-lg bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 text-xs font-medium"
        >
          + Add Custom Fee
        </button>

        {/* Invoice Advance */}
        <div className="mt-4 pt-4 border-t border-border">
          {field('Invoice Advance ($)', 'invoice_advance', 'number')}
        </div>

        {/* Legal Disclaimer */}
        <div className="mt-4">
          {textarea('Legal Disclaimer', 'legal_disclaimer')}
        </div>

        {/* Rate Breakdown */}
        <SectionHeader title="Rate Breakdown" />
        <div className="flex justify-between items-center px-4 py-3 rounded-xl bg-primary/10 border border-primary/20">
          <span className="text-sm font-semibold text-foreground">Gross Rate (Auto-calculated)</span>
          <span className="font-bold text-primary text-lg">${grossRate.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
        </div>

        {/* Rate Confirmation */}
        <SectionHeader title="Rate Confirmation" />
        <div className="space-y-2">
          {form.ratecon_url && (
            <a href={form.ratecon_url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 text-xs text-blue-400 hover:text-blue-300 transition-colors">
              <FileText className="w-3.5 h-3.5" /> View current Rate Con <ExternalLink className="w-3 h-3" />
            </a>
          )}
          <label className={cn(
            "flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed cursor-pointer transition-colors",
            rateconFile ? "border-primary/60 bg-primary/5" : "border-border hover:border-primary/40 hover:bg-white/3"
          )}>
            <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setRateconFile(e.target.files[0])} />
            {rateconFile ? <FileText className="w-5 h-5 text-primary flex-shrink-0" /> : <Upload className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
            <p className={cn("text-sm font-medium", rateconFile ? "text-primary" : "text-muted-foreground")}>
              {rateconFile ? rateconFile.name : (form.ratecon_url ? 'Replace Rate Confirmation' : 'Upload Rate Confirmation (PDF / JPG / PNG)')}
            </p>
          </label>
        </div>

        <div className="flex gap-3 pt-1">
          <Button onClick={handleSave} disabled={saving} className="flex-1 bg-primary text-primary-foreground">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button onClick={onClose} variant="outline" className="border-border">Cancel</Button>
        </div>
      </div>
      </div>
      </div>
      </>
      );
      }
